/* eslint-disable ember/classic-decorator-hooks */
import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import { cached, tracked } from '@glimmer/tracking';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import dayjs from 'dayjs';
import { task } from 'ember-concurrency';
import { tKey } from 'ember-intl';
import { OTA_IDS } from 'frontend-utils/utils/constants/constant-values';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import { DEFAULT_QP_VALUES } from 'lumen/utils/constants/default-qp-values';
import {
  DRAWER_IDS,
  MI_SEARCHES_TYPE_HOTELS_BUSINESS,
  RATE_PARAMS,
} from 'lumen/utils/constants/general-constants';
import {
  AVAILABLE_TILES_TO_ALERT_TYPES_MAPPING,
  DASHBOARD_FEED_SHOW_FILTER_KEYS,
  DASHBOARD_FEED_SORT_KEYS,
  DATA_FETCH_ERRORS_PRODUCT_NAMES,
  DEFAULT_DAYS_SHOWN_IN_OD,
  OD_ALERTS_KEYS,
  OD_ALERTS_SORT_ORDER,
  OD_ALERTS_TO_TRANSLATION_KEY_MAPPING,
  OD_ALERTS_TO_TYPES_FILTER_TRANSLATION_KEY_MAPPING,
  OD_API_DATA_KEYS,
  OD_DEFAULT_DATE_FORMAT,
  OD_FB_FILTERS,
  OD_REF_DAYS_AGO_VALUES,
  OD_SELECTED_TILES_IDS,
  OD_SETTINGS_OPTION_KEYS,
  TOP_INSIGHT_CHART_TABS,
} from 'lumen/utils/constants/overview-dashboard-constants';
import OdAlertsResponse from 'lumen/utils/datamodels/overview-dashboard/od-alerts-response';
import OdApiResponse from 'lumen/utils/datamodels/overview-dashboard/od-api-response';
import dateRangeDisplay from 'lumen/utils/dates/date-range-display';
import formatLastUpdatedTimestamp from 'lumen/utils/overview-dashboard/format-last-updated-timestamp';
import generateMarketInsightRedirectUrlQueryParams from 'lumen/utils/overview-dashboard/generate-market-insight-redirect-url-query-params';
import { parseObject, stringifyObject } from 'lumen/utils/parse-utils';
import { mapRateParamToAPIParams } from 'lumen/utils/rates-request';

export default class HotelOverviewController extends Controller {
  queryParams = [
    'fromDate',
    'toDate',
    'refDaysAgo',
    'activeFilters',
    'otaId',
    'compsetId',
    'rateParam',
  ];

  @service access;
  @service ajax;
  @service drawer;
  @service errorTracking;
  @service('hotel-and-market') hotelAndMarketService;
  @service intl;
  @service myotbSs;
  @service odAccess;
  @service odSelections;
  @service router;
  @service toast;
  @service usageAnalytics;

  @tracked activeFilters = DEFAULT_QP_VALUES.hotel.overview.activeFilters;
  @tracked compsetId;
  @tracked dashboardFeedSelectedAlertTypes = [];
  // We show both alerts and key dates by default
  @tracked dashboardFeedSelectedShowType =
    this.dashboardFeedShowFilterOptions.find(
      (filterOptions) =>
        filterOptions.id === DASHBOARD_FEED_SHOW_FILTER_KEYS.ALL,
    );
  // The alerts and key dates are sorted by stay date by default
  @tracked dashboardFeedSelectedSortType = this.dashboardFeedSortOptions.find(
    (sortOptions) => sortOptions.id === DASHBOARD_FEED_SORT_KEYS.DATE,
  );
  @tracked fromDate = null;
  @tracked hasShowFilterValueChanged = false;
  @tracked hasTypesFilterValueChanged = false;
  // Variable to track if add key date modal is visible (has no separate route)
  @tracked isShowingAddKeyDateModal = false;
  @tracked otaId = OTA_IDS.BOOKINGDOTCOM;
  @tracked rateParam = RATE_PARAMS.BEST_FLEX;
  // In the blue bar is a dropdown where users can select a reference day (e.g. vs 3 days ago)
  // This value will be used throughout the dashboard (where possible) to visualise data vs. this reference
  @tracked refDaysAgo = DEFAULT_QP_VALUES.hotel.overview.refDaysAgo;
  @tracked toDate = null;
  @tracked topInsightChartTab = '';

  createKeyDateTask = task(
    { restartable: true },
    async (selectedDate, note, source) => {
      const requestData = {
        from_date: selectedDate,
        source,
      };

      if (note) {
        requestData.text = note;
      }

      const requestParams = {
        subscription_id: this.hotelAndMarketService.hotel.id,
        // All extra data here is needed for usage tracking on BE side
        ota: this.otaId,
        compset_ids: this.compsetId,
        los: this.defaultLosRates,
        persons: this.defaultPersons,
        compare_extract_date:
          this.odSelections.refDaysAgoDatesInAPIFormat[this.refDaysAgo],
      };
      mapRateParamToAPIParams(this.rateParam, requestParams);

      try {
        const requestUrl = formatUrl(
          '/api/v3/overview/keydates/',
          requestParams,
          this.ajax.host,
        );

        await this.ajax.post(requestUrl, {
          data: JSON.stringify(requestData),
          contentType: 'application/json',
        });

        return {
          isSuccess: true,
        };
      } catch (error) {
        this.errorTracking.addBreadcrumb({ data: error });

        return {
          isSuccess: false,
          error,
        };
      }
    },
  );
  removeKeyDateTask = task({ restartable: true }, async (selectedDate) => {
    const requestData = {
      from_date: selectedDate,
    };
    const requestParams = {
      subscription_id: this.hotelAndMarketService.hotel.id,
      // All extra data here is needed for usage tracking on BE side
      ota: this.otaId,
      compset_ids: this.compsetId,
      los: this.defaultLosRates,
      persons: this.defaultPersons,
      compare_extract_date:
        this.odSelections.refDaysAgoDatesInAPIFormat[this.refDaysAgo],
    };

    mapRateParamToAPIParams(this.rateParam, requestParams);

    try {
      const requestUrl = formatUrl(
        '/api/v3/overview/keydates/',
        requestParams,
        this.ajax.host,
      );

      await this.ajax.delete(requestUrl, {
        data: JSON.stringify(requestData),
        contentType: 'application/json',
      });

      return {
        isSuccess: true,
      };
    } catch (error) {
      this.errorTracking.addBreadcrumb({ data: error });

      return {
        isSuccess: false,
        error,
      };
    }
  });

  // Task that can be used to update (or delete -> update with empty text) notes on a specific keydate
  updateKeyDateNoteTask = task(
    { restartable: true },
    async (selectedDate, note) => {
      const requestData = {
        from_date: selectedDate,
        text: note,
      };

      const requestParams = {
        subscription_id: this.hotelAndMarketService.hotel.id,
        // All extra data here is needed for usage tracking on BE side
        ota: this.otaId,
        compset_ids: this.compsetId,
        los: this.defaultLosRates,
        persons: this.defaultPersons,
        compare_extract_date:
          this.odSelections.refDaysAgoDatesInAPIFormat[this.refDaysAgo],
      };
      mapRateParamToAPIParams(this.rateParam, requestParams);

      try {
        const requestUrl = formatUrl(
          '/api/v3/overview/keydates/notes/',
          requestParams,
          this.ajax.host,
        );

        await this.ajax.post(requestUrl, {
          data: JSON.stringify(requestData),
          contentType: 'application/json',
        });

        return {
          isSuccess: true,
        };
      } catch (error) {
        this.errorTracking.addBreadcrumb({ data: error });

        return {
          isSuccess: false,
          error,
        };
      }
    },
  );

  // Required so that we do not re-run this everytime we access dashboardFeedData
  @cached
  get alertsData() {
    return this.alertsResponseData.alerts;
  }

  // This response contains all alerts/key dates/meta info
  @cached
  get alertsResponseData() {
    if (this.dataToUse) {
      const data = OdAlertsResponse.fromApiResponse(this.dataToUse);

      // sort alerts and key dates based on decreasing number of alerts
      if (
        this.dashboardFeedSelectedSortType.id ===
        DASHBOARD_FEED_SORT_KEYS.NUMBER_OF_ALERTS
      ) {
        data.alerts.sort(
          (alert1, alert2) => alert2.alerts.length - alert1.alerts.length,
        );
        data.keyDates.sort(
          (keyDate1, keyDate2) =>
            keyDate2.alerts.length - keyDate1.alerts.length,
        );
      }

      return data;
    }

    return OdAlertsResponse.createEmptyResponse();
  }

  // Get keys of all channels for which we need to show a ranking tile
  get availableRankingAndParityOtaIds() {
    if (this.parityData) {
      const rankingOtas = this.hotelAndMarketService.hotel.OTAsRanking.map(
        (rankingOta) => rankingOta.id,
      );
      const parityOtas = Object.keys(this.parityData);

      const uniqueOtaIds = [...new Set([...rankingOtas, ...parityOtas])];

      // Calculate the number of blocks (ranking / review / parity) each OTA tile will have
      const numberOfBlocksPerOtaTile = uniqueOtaIds.map((otaId) => {
        // If otaId is present in both ranking and parity data, then it'll have three blocks (ranking, review & parity)
        if (rankingOtas.includes(otaId) && parityOtas.includes(otaId)) {
          return { id: otaId, blocks: 3 };
        }

        // If otaId is present only in ranking data, then it'll have two blocks (ranking & review)
        if (rankingOtas.includes(otaId) && !parityOtas.includes(otaId)) {
          return { id: otaId, blocks: 2 };
        }

        // If otaId is present only in parity data, then it'll only have one block (parity)
        return { id: otaId, blocks: 1 };
      });

      // We sort the available rankingAndParity tiles in the descending order of the number of blocks they contain
      return numberOfBlocksPerOtaTile
        .sort((otaTile1, otaTile2) => otaTile2.blocks - otaTile1.blocks)
        .map((ota) => ota.id);
    }

    return this.hotelAndMarketService.hotel.OTAsRanking.map(
      (rankingOta) => rankingOta.id,
    );
  }

  // Required so that we do not re-run this everytime we access dashboardFeedData
  @cached
  get combinedAlertsKeydatesData() {
    // Show all alerts + key dates with no alerts (key dates added manually) + key dates outside date range (as they are not returned in the alerts response data)
    const keyDatesWithoutAlertsListOrOutsideDateRange =
      this.keyDatesData.filter(
        (keyDate) =>
          keyDate.alerts.length === 0 ||
          this.hotelAndMarketService
            .toLocalTimeWithFallback(keyDate.fromDate)
            .isBefore(this.datePickerRange.start, 'day') ||
          this.hotelAndMarketService
            .toLocalTimeWithFallback(keyDate.fromDate)
            .isAfter(this.datePickerRange.end, 'day'),
      );

    const combinedAlertsKeydatesList = [
      ...keyDatesWithoutAlertsListOrOutsideDateRange,
      ...this.alertsData,
    ];

    // Sort the combined list based on fromDate
    combinedAlertsKeydatesList.sort((feedEntry1, feedEntry2) => {
      const fromDate1 = new Date(feedEntry1.fromDate);
      const fromDate2 = new Date(feedEntry2.fromDate);

      return fromDate1 - fromDate2;
    });

    return combinedAlertsKeydatesList;
  }

  get combinedDataForEventsTile() {
    const data = {};
    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.events)) {
      data.eventsData = this.eventsDataWithDaySelectionIntoAccount;
      data.keyDates = this.alertsResponseData?.keyDates;

      if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb)) {
        data.otbData = this.otbData;
      }

      if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.demand_level)) {
        data.marketDemandData =
          this.marketDemandDataWithDaySelectionIntoAccount;
      }
    }
    return data;
  }

  get dailyPatternsOtbData() {
    if (
      this.model.dailyPatternsOtbDataRequestTask &&
      !this.model.dailyPatternsOtbDataRequestTask.isRunning &&
      this.model.dailyPatternsOtbDataRequestTask.value
    ) {
      return this.model.dailyPatternsOtbDataRequestTask.value;
    }

    return OdApiResponse.createEmptyResponse();
  }

  get dailyPatternsPriceOpportunityData() {
    if (!this.formattedMarketDemandData) {
      return [];
    }

    if (
      !this.dailyPatternsPricePositionData.fromDates.some(
        (position) => position.price,
      )
    ) {
      return [];
    }

    const opportunities = this.formattedMarketDemandData?.fromDates?.map(
      (demand) => ({
        date: demand.fromDate,
        hasOpportunity: demand.hasOpportunity,
        opportunity: demand.opportunity,
      }),
    );

    return opportunities;
  }

  get dailyPatternsPricePositionData() {
    if (
      this.model.dailyPatternsPricePositionDataRequestTask &&
      !this.model.dailyPatternsPricePositionDataRequestTask.isRunning &&
      this.model.dailyPatternsPricePositionDataRequestTask.value
    ) {
      return this.model.dailyPatternsPricePositionDataRequestTask.value;
    }
    return OdApiResponse.createEmptyResponse();
  }

  get dailyPatternsUpdatedIndicatorContent() {
    if (
      this.otbUpdateIndicatorContent ||
      this.marketOtbUpdateIndicatorContent
    ) {
      let formattedOtbLastUpdatedTimestamp = '';
      let formattedMarketOtbLastUpdatedTimestamp = '';
      let formattedLastUpdatedTimestamp = '';
      let isOutOfSync;

      // Case 1: Only My OTB. no Market OTB present
      if (this.otbUpdateIndicatorContent) {
        formattedOtbLastUpdatedTimestamp =
          this.otbUpdateIndicatorContent.formattedLastUpdatedTimestamp;
        formattedLastUpdatedTimestamp += `${this.intl.t(
          'rates.my_OTB',
        )}: ${formattedOtbLastUpdatedTimestamp}`;
        isOutOfSync = this.otbUpdateIndicatorContent.isOutOfSync;

        // Case 2: Both My OTB and Market OTB present
        if (this.marketOtbUpdateIndicatorContent) {
          formattedMarketOtbLastUpdatedTimestamp =
            this.marketOtbUpdateIndicatorContent.formattedLastUpdatedTimestamp;
          formattedLastUpdatedTimestamp += `<br><br>${this.intl.t(
            'rates.tooltip.market_otb',
          )}: ${formattedMarketOtbLastUpdatedTimestamp}`;
          isOutOfSync =
            isOutOfSync || this.marketOtbUpdateIndicatorContent.isOutOfSync;
        }
      }

      return {
        isOutOfSync,
        formattedLastUpdatedTimestamp: htmlSafe(formattedLastUpdatedTimestamp),
      };
    }

    // neither otb or market otb
    return null;
  }

  get dashboardFeedData() {
    let dataToBeShown = [];

    // Apply Show filter
    dataToBeShown = this.dashboardFeedDataAfterShowFilter;
    dataToBeShown = dataToBeShown.map((alertsData) => {
      // Filter the alerts that have a translation mapping
      // This is added to prevent dashboard from breaking when new alerts are added by BE which aren't supported by FE yet
      alertsData.alerts = alertsData.alerts.filter(
        (alert) => alert.name in OD_ALERTS_TO_TRANSLATION_KEY_MAPPING,
      );

      // Filter alerts of type price opportunity with no rate
      alertsData.alerts = alertsData.alerts.filter(
        (alert) =>
          alert.name !== OD_ALERTS_KEYS.price_opportunity ||
          Boolean(alertsData.currentPrice),
      );
      return alertsData;
    });

    dataToBeShown = dataToBeShown.filter(
      (alertsData) => alertsData.alerts.length > 0 || alertsData.isKeyDate,
    );

    // Apply Types filter
    // In case All is selected, dashboardFeedSelectedAlertTypes is an empty array and therefore no filtering happens
    if (this.dashboardFeedSelectedAlertTypes.length) {
      dataToBeShown = HotelOverviewController.filterAlertsByTypes(
        dataToBeShown,
        this.dashboardFeedSelectedAlertTypes,
      );
    }

    // Apply sorting filter on the above returned data
    if (
      this.dashboardFeedSelectedSortType.id ===
      DASHBOARD_FEED_SORT_KEYS.NUMBER_OF_ALERTS
    ) {
      dataToBeShown.sort(
        (alert1, alert2) => alert2.alerts.length - alert1.alerts.length,
      );
    }

    return dataToBeShown;
  }

  get dashboardFeedDataAfterShowFilter() {
    let showData = [];

    // Apply Show filter
    if (
      this.dashboardFeedSelectedShowType.id ===
      DASHBOARD_FEED_SHOW_FILTER_KEYS.ALL
    ) {
      showData = this.combinedAlertsKeydatesData;
    } else if (
      this.dashboardFeedSelectedShowType.id ===
      DASHBOARD_FEED_SHOW_FILTER_KEYS.ALERTS
    ) {
      showData = this.alertsData;
    } else if (
      this.dashboardFeedSelectedShowType.id ===
      DASHBOARD_FEED_SHOW_FILTER_KEYS.FAVOURITES
    ) {
      showData = this.keyDatesData;
    }

    return showData;
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get dashboardFeedShowFilterOptions() {
    return [
      {
        id: DASHBOARD_FEED_SHOW_FILTER_KEYS.ALL,
        label: this.intl.t('overview_dashboard.show_filter.options.all'),
      },
      {
        id: DASHBOARD_FEED_SHOW_FILTER_KEYS.ALERTS,
        label: this.intl.t('overview_dashboard.feed.alerts'),
        icon: 'notification',
      },
      {
        id: DASHBOARD_FEED_SHOW_FILTER_KEYS.FAVOURITES,
        label: this.intl.t('overview_dashboard.feed.favourites'),
        icon: 'star-filled',
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get dashboardFeedSortOptions() {
    return [
      {
        id: DASHBOARD_FEED_SORT_KEYS.DATE,
        label: this.intl.t('general.date'),
      },
      {
        id: DASHBOARD_FEED_SORT_KEYS.NUMBER_OF_ALERTS,
        label: this.intl.t(
          'overview_dashboard.alerts_sort_options.number_of_alerts',
        ),
      },
    ];
  }

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get dashboardFeedTypesFilterOptions() {
    // filter out the available tiles that map to a possible alert type as not all tiles leads to an alert type
    let alertTypes = this.odAccess.availableTiles
      .filter((tile) =>
        Object.keys(AVAILABLE_TILES_TO_ALERT_TYPES_MAPPING).includes(tile),
      )
      .map((type) => AVAILABLE_TILES_TO_ALERT_TYPES_MAPPING[type])
      .flat();

    if (!this.odAccess.isMarketInsightFull) {
      alertTypes = alertTypes.filter(
        (alert) => alert !== OD_ALERTS_KEYS.price_opportunity,
      );
    }

    const possibleOptions = [];
    alertTypes.forEach((type) => {
      possibleOptions.push({
        id: type,
        label: this.intl.t(
          OD_ALERTS_TO_TYPES_FILTER_TRANSLATION_KEY_MAPPING[type],
        ),
        badge: this.numberOfAlertsByTypeObject[type].toString(),
      });
    });

    // sort the possible Options
    possibleOptions.sort(
      (a, b) => OD_ALERTS_SORT_ORDER[a.id] - OD_ALERTS_SORT_ORDER[b.id],
    );

    return possibleOptions;
  }

  get dashboardFeedTypesFilterValue() {
    // An empty array means that all types are selected
    if (this.dashboardFeedSelectedAlertTypes.length === 0) {
      return this.dashboardFeedTypesFilterOptions;
    }

    return this.dashboardFeedSelectedAlertTypes.map((optionId) => {
      return this.dashboardFeedTypesFilterOptions.find(
        (option) => option.id === optionId,
      );
    });
  }

  get dataToUse() {
    if (!this.model.requestAlertsDataTask.isRunning) {
      if (this.model.requestAlertsDataTask.last.value) {
        return this.model.requestAlertsDataTask.last.value;
      }
    }

    if (this.model.requestAlertsDataTask.lastComplete?.value) {
      return this.model.requestAlertsDataTask.lastComplete?.value;
    }

    return null;
  }

  get datePickerMaxDate() {
    return this.hotelAndMarketService.getLocalTime().add(1, 'year');
  }

  get datePickerMinDate() {
    return this.hotelAndMarketService.getLocalTime().startOf('month');
  }

  get datePickerRange() {
    return {
      start: this.fromDateMoment,
      end: this.toDateMoment,
    };
  }

  get datePickerRangeOptions() {
    const todayMoment = this.hotelAndMarketService.getLocalTime();

    return [
      {
        options: [
          {
            text: 'revenue_insight.current_month',
            id: 'current_month',
            isSelected: false,
            range: {
              start: todayMoment.startOf('month'),
              end: todayMoment.endOf('month'),
            },
          },
          {
            text: 'general.next_days',
            count: 30,
            id: 'thirty_days',
            isSelected: true,
            range: {
              start: todayMoment,
              end: todayMoment.add(30, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 60,
            id: 'sixty_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(60, 'day'),
            },
          },
          {
            text: 'general.next_days',
            count: 90,
            id: 'ninety_days',
            isSelected: false,
            range: {
              start: todayMoment,
              end: todayMoment.add(90, 'day'),
            },
          },
          {
            text: 'general.custom',
            id: 'custom',
            isSelected: false,
            range: {
              start: null,
              end: null,
            },
          },
        ],
      },
    ];
  }

  get defaultLosRates() {
    return this.hotelAndMarketService.hotelOrMarket?.defaultLosRates?.[0];
  }

  get defaultPersons() {
    return this.hotelAndMarketService.hotelOrMarket?.defaultPersons;
  }

  get eventsData() {
    return this.model?.eventsDataRequestTask?.value;
  }

  get eventsDataWithDaySelectionIntoAccount() {
    if (!this.model.eventsDataRequestTask.isRunning && this.eventsData) {
      if (this.odSelections.isDayDrilldownActive) {
        const selectedDayData = this.eventsData.fromDates.filter(
          (fromDate) =>
            fromDate.fromDate === this.odSelections.activeDrilldownDayAsString,
        );

        return selectedDayData;
      }
      return this.eventsData.fromDates;
    }

    return [];
  }

  // In case a service fetch fails in the alerts request, its key is added to the data_fetch_errors in the meta object of the alerts response
  // However, this information is used to show the service unavailability message for the entire dashboard
  get formattedDataFetchErrorMessage() {
    let message = null;

    const dataFetchErrorsSources =
      this.alertsResponseData.meta?.data_fetch_errors;
    const dataFetchErrorsSourcesProductNames = [
      ...new Set(
        dataFetchErrorsSources?.map(
          (source) => DATA_FETCH_ERRORS_PRODUCT_NAMES[source],
        ),
      ),
    ];

    if (dataFetchErrorsSourcesProductNames.length) {
      message = this.intl.t(
        'overview_dashboard.product_unavailability.message',
        {
          products: dataFetchErrorsSourcesProductNames.join(', '),
        },
      );
    }

    return message;
  }

  // Formatted date range that has a fallback to today. Can be used to indicate active daterange for kpis that don't support data in the past
  get formattedDateRangeForKpisWithForwardLookingData() {
    // In case of all dates in the past, we can't show any date range
    if (
      this.odSelections.dateRangeForKpisWithForwardLookingData.start === null &&
      this.odSelections.dateRangeForKpisWithForwardLookingData.end === null
    ) {
      return this.intl.t('general.missing');
    }

    return dateRangeDisplay(
      dayjs(this.odSelections.dateRangeForKpisWithForwardLookingData.start),
      dayjs(this.odSelections.dateRangeForKpisWithForwardLookingData.end),
      this.intl,
    );
  }

  // Formatted date range that basically fully follows the range of the datepicker (can go in the past if the user selects the "Current month" option)
  get formattedDateRangeForKpisWithPastDataAvailable() {
    return dateRangeDisplay(
      this.datePickerRange.start,
      this.datePickerRange.end,
      this.intl,
    );
  }

  /* It shouldn't be possible to see the opportunities if there are no rates because the user will
   * not have a source to make decisions. For that we decided to check the rates data (priceDifferenceData)
   * and if the own hotel doesn't have a rate for that day we don't show any opportunity
   */
  get formattedMarketDemandData() {
    if (!this.marketDemandData) {
      return [];
    }

    // if there are no rates is not necesary to add the opportunities because they can't be shown anyway
    if (!this.dailyPatternsPricePositionData) {
      return this.marketDemandData;
    }

    const formattedMarketDemandData = this.marketDemandData;

    formattedMarketDemandData.fromDates.forEach((marketDemand, index) => {
      marketDemand.hasOpportunity =
        marketDemand.kpis[
          `price_opportunity.new.${this.odSelections.refDaysAgoDatesInAPIFormat[this.refDaysAgo]}`
        ];

      if (
        this.priceDifferenceData.fromDates[index] &&
        this.priceDifferenceData.fromDates[index].kpis.rates[
          this.hotelAndMarketService.hotel.hotelinfo.id
        ][0].value === 0
      ) {
        marketDemand.hasOpportunity = false;
      }
    });

    return formattedMarketDemandData;
  }

  get fromDateMoment() {
    return this.hotelAndMarketService.toLocalTimeWithFallback(this.fromDate);
  }

  get hotel() {
    return this.hotelAndMarketService.hotel;
  }

  get isDailyPatternsDataLoading() {
    return (
      this.model?.dailyPatternsOtbDataRequestTask?.isRunning ||
      this.model?.dailyPatternsPricePositionDataRequestTask?.isRunning ||
      this.model?.marketDemandDataRequestTask?.isRunning ||
      this.model?.priceDifferenceDataRequestTask?.isRunning ||
      this.model?.rentalDataRequestTask?.isRunning
    );
  }

  get isDailyPatternsUpdateIndicatorVisible() {
    return (
      !this.isDailyPatternsDataLoading &&
      (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb) ||
        this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.market_otb))
    );
  }

  // Events KPI has a more complex loading behaviour (can depend on two separate data requests in case the subscription has OTB)
  get isDataForEventsTileLoading() {
    let isLoading = null;

    if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.events)) {
      if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb)) {
        isLoading =
          this.model.eventsDataRequestTask.isRunning ||
          this.model.otbDataRequestTask.isRunning;
      } else {
        isLoading = this.model.eventsDataRequestTask.isRunning;
      }
    }

    return isLoading;
  }

  get isDrawerCardFooterVisible() {
    return (
      this.dashboardFeedData.length &&
      (this.dashboardFeedSelectedShowType.id ===
        DASHBOARD_FEED_SHOW_FILTER_KEYS.ALL ||
        this.dashboardFeedSelectedShowType.id ===
          DASHBOARD_FEED_SHOW_FILTER_KEYS.FAVOURITES)
    );
  }

  get isPriceDifferenceLoading() {
    return this.model?.priceDifferenceDataRequestTask?.isRunning;
  }

  get isRankingAndParityDataLoading() {
    return (
      this.model?.rankingDataRequestTask?.isRunning ||
      this.model?.parityDataRequestTask?.isRunning
    );
  }

  get isRatesDataLoading() {
    return (
      this.model?.priceDifferenceDataRequestTask?.isRunning ||
      this.model?.otbDataRequestTask?.isRunning ||
      this.model?.rentalDataRequestTask?.isRunning
    );
  }

  get isTopSearchingCountriesAvailable() {
    // Show Top Searching countries if there's an empty response (outside of MI date range)
    if (this.marketDemandDataWithDaySelectionIntoAccount.isEmptyResponse) {
      return true;
    }

    // Show Top Searching countries if there are search summaries but don't show it if the only search summary is Hotels GDS
    const searchSummaries =
      this.marketDemandDataWithDaySelectionIntoAccount?.kpis?.searchsummaries;
    return (
      searchSummaries?.length > 0 &&
      !(
        searchSummaries?.length === 1 &&
        searchSummaries[0].type === MI_SEARCHES_TYPE_HOTELS_BUSINESS
      )
    );
  }

  // Required so that we do not re-run this everytime we access dashboardFeedData
  @cached
  get keyDatesData() {
    return this.alertsResponseData.keyDates;
  }

  get kpiRedirectUrlQueryParamsForSmartCompset() {
    const refDaysAgo = this.odSelections.selectedRefDaysAgo.id;

    const activeDrilldownDayMoment = this.odSelections.isDayDrilldownActive
      ? dayjs(this.odSelections.activeDrilldownDayAsString)
      : null;

    return generateMarketInsightRedirectUrlQueryParams(
      this.datePickerRange.start,
      this.datePickerRange.end,
      refDaysAgo,
      activeDrilldownDayMoment,
      'price-level-distribution',
    );
  }

  // Transform the parsed filters to the format the FilterBar component expects
  get mappedActiveFilters() {
    const mappedActiveFilters = [];

    Object.keys(this.parsedActiveFilters).forEach((filterKey) => {
      const filterValue = this.parsedActiveFilters[filterKey];
      mappedActiveFilters.push({
        key: filterKey,
        translationKey: `market_insight.filters.${filterKey}`, // ED TODO: add translations
        values: [
          {
            key: filterValue,
            displayValue: HotelOverviewController.filterValueFormatter(
              filterKey,
              filterValue,
              this.intl,
            ),
          },
        ],
      });
    });

    return mappedActiveFilters;
  }

  get marketDemandData() {
    return this.model?.marketDemandDataRequestTask?.last?.value;
  }

  get marketDemandDataWithDaySelectionIntoAccount() {
    // Extra this.marketDemandData check because the API request to handle the case of failing API request
    if (
      !this.model.marketDemandDataRequestTask?.isRunning &&
      this.marketDemandData
    ) {
      if (this.odSelections.isDayDrilldownActive) {
        const selectedDayData = this.formattedMarketDemandData.fromDates.find(
          (fromDateValues) =>
            fromDateValues.fromDate ===
            this.odSelections.activeDrilldownDayAsString,
        );
        if (selectedDayData) {
          // Note: mimic the data structure of the original API request (so the tile component only needs to support one)
          return selectedDayData;
        }
        return OdApiResponse.createEmptyResponse();
      }
      return this.formattedMarketDemandData;
    }

    return OdApiResponse.createEmptyResponse();
  }

  get marketDemandHasOpportunityPrimaryAction() {
    return {
      label: this.intl.t('overview_dashboard.market.see_smart_compset_pricing'),
      action: this.onBannerOpportunityMessageClick,
    };
  }

  get marketOtbUpdateIndicatorContent() {
    if (this.otbDataWithDaySelectionIntoAccount) {
      const isOutOfSync =
        this.otbDataWithDaySelectionIntoAccount.isDataOutOfSync(
          OD_API_DATA_KEYS.market_otb_percentage,
        );
      const lastUpdatedTimestamp =
        this.otbDataWithDaySelectionIntoAccount.getDataLastUpdateTimestamp(
          OD_API_DATA_KEYS.market_otb_percentage,
        );

      if (lastUpdatedTimestamp) {
        return {
          isOutOfSync,
          formattedLastUpdatedTimestamp: formatLastUpdatedTimestamp(
            this.intl,
            lastUpdatedTimestamp,
          ),
        };
      }
    }

    return null;
  }

  get numberOfAlertsByTypeObject() {
    const alertsByTypeObject = {};

    // Create object that stores the number of alerts for each alert type. This can be used to display badges in the Type filter dropdown.
    Object.keys(OD_ALERTS_KEYS).forEach((alertType) => {
      const numberOfAlerts = HotelOverviewController.filterAlertsByTypes(
        this.dashboardFeedDataAfterShowFilter,
        [alertType],
      ).length;

      alertsByTypeObject[alertType] = numberOfAlerts;
    });

    return alertsByTypeObject;
  }

  get numberOfCompetitors() {
    // Filter out the hotels that are removed i.e. have no matching with the selected OTA (same behavior as in Rates tab)
    const filteredActiveCompetitors =
      this.hotelAndMarketService.activeCompetitors.filter(
        (hotelInfo) =>
          hotelInfo.otas.find((ota) => ota.id === this.otaId) !== undefined,
      );
    return filteredActiveCompetitors.length;
  }

  // Note: this is a getter to make use of this.intl
  get odSettingsOptions() {
    return [
      {
        id: OD_SETTINGS_OPTION_KEYS.SETTINGS_MODAL,
        label: this.intl.t('overview_dashboard.settings.modal.title'),
      },
    ];
  }

  get odSettingsValue() {
    return {
      icon: 'settings',
    };
  }

  get otbData() {
    return this.model?.otbDataRequestTask?.value;
  }

  // OTB data that gets passed to certain KPI cards for visualisation
  // Takes the "isDayDrilldownActive" into account. In that case we filter out the data of the selected day (should always be in the data range we've requested in the route)
  get otbDataWithDaySelectionIntoAccount() {
    if (
      this.model.otbDataRequestTask &&
      !this.model.otbDataRequestTask.isRunning &&
      this.otbData
    ) {
      if (this.odSelections.isDayDrilldownActive) {
        const selectedDayData = this.otbData.fromDates.find(
          (fromDateValues) =>
            fromDateValues.fromDate ===
            this.odSelections.activeDrilldownDayAsString,
        );

        // Note: mimic the data structure of the original API request (so the tile component only needs to support one)
        if (selectedDayData) {
          return selectedDayData;
        }
        return OdApiResponse.createEmptyResponse();
      }

      return this.otbData;
    }

    return OdApiResponse.createEmptyResponse();
  }

  get otbUpdateIndicatorContent() {
    if (this.otbDataWithDaySelectionIntoAccount) {
      const isOutOfSync =
        this.otbDataWithDaySelectionIntoAccount.isDataOutOfSync(
          OD_API_DATA_KEYS.my_otb_percentage,
        );
      const lastUpdatedTimestamp =
        this.otbDataWithDaySelectionIntoAccount.getDataLastUpdateTimestamp(
          OD_API_DATA_KEYS.my_otb_percentage,
        );

      if (lastUpdatedTimestamp) {
        return {
          isOutOfSync,
          formattedLastUpdatedTimestamp: formatLastUpdatedTimestamp(
            this.intl,
            lastUpdatedTimestamp,
          ),
        };
      }
    }

    return null;
  }

  get parityData() {
    return this.model?.parityDataRequestTask?.value;
  }

  get parsedActiveFilters() {
    if (this.activeFilters) {
      return parseObject(this.activeFilters);
    }
    return {};
  }

  get priceDifferenceData() {
    if (
      this.model.priceDifferenceDataRequestTask &&
      !this.model.priceDifferenceDataRequestTask.isRunning &&
      this.model.priceDifferenceDataRequestTask.value
    ) {
      return this.model.priceDifferenceDataRequestTask.value;
    }

    return OdApiResponse.createEmptyResponse();
  }

  // Getter used to provide data for the Price difference daily view component
  // The Price difference aggregated component uses the full priceDifferenceData directly
  get priceDifferenceDataWithSingleDaySelectionIntoAccount() {
    if (
      !this.model.priceDifferenceDataRequestTask.isRunning &&
      this.priceDifferenceData
    ) {
      if (
        this.odSelections.isDayDrilldownActive ||
        this.odSelections.isSingleDaySelectedInTheDatepicker
      ) {
        const selectedDay = this.odSelections.isDayDrilldownActive
          ? this.odSelections.activeDrilldownDayAsString
          : this.odSelections.datepickerFromDate;

        const fromDateData = this.priceDifferenceData.fromDates.find(
          (fromDateValues) => fromDateValues.fromDate === selectedDay,
        );

        if (fromDateData) {
          return fromDateData;
        }
        return null;
      }
    }

    return null;
  }

  get rankingData() {
    return this.model?.rankingDataRequestTask?.value;
  }

  // Note: this is a getter to make use of this.intl
  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get refDaysAgoSelectionOptions() {
    return [
      {
        id: OD_REF_DAYS_AGO_VALUES.yesterday,
        label: this.intl.t('general.daysago', { count: 1 }),
      },
      {
        id: OD_REF_DAYS_AGO_VALUES.threeDaysAgo,
        label: this.intl.t('general.daysago', { count: 3 }),
      },
      {
        id: OD_REF_DAYS_AGO_VALUES.sevenDaysAgo,
        label: this.intl.t('general.daysago', { count: 7 }),
      },
      {
        id: OD_REF_DAYS_AGO_VALUES.fourteenDaysAgo,
        label: this.intl.t('general.daysago', { count: 14 }),
      },
    ];
  }

  get rentalData() {
    // In case rentalDataRequestTask is not available it's returned an empty response
    // to avoid returning undefined when rentalData is used
    if (this.model?.rentalDataRequestTask?.isRunning) {
      return OdApiResponse.createEmptyResponse();
    }
    return this.model?.rentalDataRequestTask?.value;
  }

  get revenueData() {
    return this.model?.revenueDataRequestTask?.value;
  }

  get revenueDataWithDaySelectionIntoAccount() {
    if (!this.model.revenueDataRequestTask.isRunning && this.revenueData) {
      if (this.odSelections.isDayDrilldownActive) {
        const selectedDayData = this.revenueData.fromDates.find(
          (fromDateValues) =>
            fromDateValues.fromDate ===
            this.odSelections.activeDrilldownDayAsString,
        );

        // Note: mimic the data structure of the original API request (so the tile component only needs to support one)
        if (selectedDayData) {
          return selectedDayData;
        }
        return OdApiResponse.createEmptyResponse();
      }

      return this.revenueData;
    }

    return OdApiResponse.createEmptyResponse();
  }

  get selectedRefDaysAgo() {
    return this.refDaysAgoSelectionOptions.find(
      (refDaysAgoOption) => refDaysAgoOption.id === this.refDaysAgo,
    );
  }

  get toDateMoment() {
    if (this.toDate) {
      return this.hotelAndMarketService.toLocalTime(this.toDate);
    }
    return this.hotelAndMarketService
      .getLocalTime()
      .add(DEFAULT_DAYS_SHOWN_IN_OD, 'day');
  }

  init() {
    super.init(...arguments);

    this.drawer.expandDrawer(DRAWER_IDS.OD_DASHBOARD_FEED);
  }

  static filterAlertsByTypes(data, selectedAlertTypes) {
    return data.filter((dayData) => {
      const dayDataAlertTypes = dayData.alerts.map((alert) => alert.name);

      return dayDataAlertTypes.some((type) =>
        selectedAlertTypes.includes(type),
      );
    });
  }

  static filterValueFormatter(filterKey, filterValue) {
    switch (filterKey) {
      case OD_FB_FILTERS.DAY: {
        return dayjs(filterValue).format(OD_DEFAULT_DATE_FORMAT);
      }
      default: {
        return filterValue;
      }
    }
  }

  // Certain actions can result in the removing of day filters/undoing of expansions
  resetDayDrilldown() {
    // Remove day drilldown in case one is currently active
    if (this.odSelections.isDayDrilldownActive) {
      this.clearActiveFilter(
        OD_FB_FILTERS.DAY,
        this.odSelections.activeDrilldownDayAsString,
      );
    }
  }

  @action
  clearActiveFilter(filterId, filterValue) {
    const currentFilters = this.parsedActiveFilters;
    if (currentFilters[filterId] === filterValue) {
      delete currentFilters[filterId];
    }
    this.activeFilters = stringifyObject(currentFilters);
    // Also update in selections service
    this.odSelections.activeFilters = this.activeFilters;
  }

  @action
  closeAddKeyDateModal() {
    this.isShowingAddKeyDateModal = false;
  }

  @action
  dashboardFeedAlertTypesBuildLabelWithCount(count) {
    return this.intl.t('overview_dashboard.feed.types_filter.types.count', {
      count,
    });
  }

  @action
  dashboardFeedAlertTypesChanged(selection) {
    this.hasTypesFilterValueChanged = true;

    // Empty array = everything selected
    if (selection.length === this.dashboardFeedTypesFilterOptions.length) {
      this.dashboardFeedSelectedAlertTypes = [];
    }

    this.dashboardFeedSelectedAlertTypes = selection.map(
      (selectedType) => selectedType.id,
    );
  }

  @action
  dashboardFeedShowTypeChanged(showType) {
    this.hasShowFilterValueChanged = true;

    this.dashboardFeedSelectedShowType = showType;
  }

  @action
  dashboardFeedSortTypeChanged(sortType) {
    this.dashboardFeedSelectedSortType = sortType;
  }

  // Done once on page load. Initialises needed values in the odService
  @action
  initODSelectionsService() {
    this.odSelections.activeFilters = this.activeFilters;
    this.odSelections.selectedRefDaysAgo = this.selectedRefDaysAgo;
  }

  @action
  onBannerOpportunityMessageClick() {
    this.router.transitionTo('hotel.market-insight', {
      model: this.hotelAndMarketService.hotel.id,
      queryParams: { ...this.kpiRedirectUrlQueryParamsForSmartCompset },
    });
  }

  @action
  onDashboardFeedDrawerToggle() {
    this.drawer.toggleDrawerState(DRAWER_IDS.OD_DASHBOARD_FEED);
  }

  /**
   * This action is performed when the user clicks the keydate star button either from the events tile or the alerts/keyDates feed entry
   * @param { Object } dayToUpdate: can be alerts or keydate object, or a custom object containing `isKeyDate` & `fromDate` data (e.g. events KPI)
   * @param { String } note: note to be added to the keydate during the keydate creation
   * @param ( String } source: indicates from where the keydate star click originates (needed for BE tracking purposes)
   */
  @action
  async onKeyDateStarClicked(dayToUpdate, note, source) {
    let result = null;

    const isKeyDateAfterUpdate = !dayToUpdate.isKeyDate;

    if (isKeyDateAfterUpdate) {
      result = await this.createKeyDateTask.perform(
        dayToUpdate.fromDate,
        note,
        source,
      );
    } else {
      result = await this.removeKeyDateTask.perform(dayToUpdate.fromDate);
    }

    if (result.isSuccess) {
      // Show fitting success message depending on the action taken
      const toastTitleTranslationKey = isKeyDateAfterUpdate
        ? 'overview_dashboard.key_date.add_key_date.message.success.title'
        : tKey(
            'overview_dashboard.key_date.remove_key_date.message.success.title',
          );

      this.toast.success('', {
        title: this.intl.t(toastTitleTranslationKey),
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });

      // Refresh the feed after the change
      this.relaunchAlertsDataRequest();
    } else {
      // Show fitting error message depending on the action taken
      const toastTitleTranslationKey = isKeyDateAfterUpdate
        ? 'overview_dashboard.key_date.add_key_date.message.error.title'
        : tKey(
            'overview_dashboard.key_date.remove_key_date.message.error.title',
          );

      this.toast.alert('', {
        title: this.intl.t(toastTitleTranslationKey),
        timeout: TOAST_TIMEOUT_LONG,
        showIcon: true,
      });
    }
  }

  @action
  onRefDaysAgoSelectionChanged(selectedReference) {
    this.refDaysAgo = selectedReference.id;

    // Also update in selections service
    this.odSelections.selectedRefDaysAgo = selectedReference;

    // RefDays is a URL parameter, but doesn't retrigger a full page reload. We do however need to re-request the alerts data
    // We only get the date for 1 reference data here, instead of for all possible reference dates in the KPI requests
    this.relaunchAlertsDataRequest();
  }

  @action
  onSettingsSelect(selectedOption) {
    if (selectedOption.id === OD_SETTINGS_OPTION_KEYS.SETTINGS_MODAL) {
      this.router.legacyTransitionTo('hotel.overview.settings');
    }
  }

  @action
  openAddKeyDateModal() {
    this.isShowingAddKeyDateModal = true;
  }

  @action
  rangeChanged(range) {
    this.resetDayDrilldown();

    if (range.start) {
      this.fromDate = range.start.format('YYYY-MM-DD');
    }
    if (range.end) {
      this.toDate = range.end.format('YYYY-MM-DD');
    } else {
      this.toDate = range.start.format('YYYY-MM-DD');
    }
  }

  @action
  relaunchAlertsDataRequest() {
    this.model.requestAlertsDataTask.perform(
      this.odSelections.dateRangeForKpisWithForwardLookingData.start,
      this.odSelections.dateRangeForKpisWithForwardLookingData.end,
      this.otaId,
      this.compsetId,
      this.rateParam,
      this.refDaysAgo,
    );
  }

  @action
  selectOta(otaToSelect) {
    this.otaId = otaToSelect.id;
  }

  @action
  selectRateType(rateTypeToSelect) {
    this.rateParam = rateTypeToSelect.id;
  }

  @action
  setActiveFilters(filterId, filterValue) {
    // Update the current filters
    const currentFilters = this.parsedActiveFilters;
    currentFilters[filterId] = filterValue;

    this.activeFilters = stringifyObject(currentFilters);
    // Also update in selections service
    this.odSelections.activeFilters = this.activeFilters;
  }

  @action
  setCompsetId(compsetId) {
    this.compsetId = compsetId;
  }

  @action
  setTopInsightDefaultChartTab() {
    // Case 1: OTB not integrated
    // - Case 1.1: If OTB SS entry points not dismissed -> Performance
    // -Case 1.2: If OTB SS entry points dismissed -> Rates
    // Case 2: OTB integrated -> Performance
    // Case 3: OTB integration not possible (exclusion list) -> Remove Performance, only Rates tab available
    if (this.access.hasFeature('otb.empty_placeholder')) {
      if (this.myotbSs.isOtbSelfServiceUpsellBannerVisible) {
        this.topInsightChartTab = TOP_INSIGHT_CHART_TABS.PERFORMANCE;
      } else {
        this.topInsightChartTab = TOP_INSIGHT_CHART_TABS.RATES;
      }
    } else if (this.odAccess.isTileVisible(OD_SELECTED_TILES_IDS.my_otb)) {
      this.topInsightChartTab = TOP_INSIGHT_CHART_TABS.PERFORMANCE;
    } else {
      this.topInsightChartTab = TOP_INSIGHT_CHART_TABS.RATES;
    }
  }

  @action
  toggleOrSetSelectedDay(date) {
    if (this.odSelections.isDayDrilldownActive) {
      if (date !== this.odSelections.activeDrilldownDayAsString) {
        this.updateSelectedDay(date);
      } else {
        this.clearActiveFilter(
          OD_FB_FILTERS.DAY,
          this.odSelections.activeDrilldownDayAsString,
        );
      }
    } else {
      this.updateSelectedDay(date);
    }
  }

  @action
  triggerDayDrilldownOutsideSelectedRange(date) {
    // Extra logic to make sure we get sort of consistent behavior for keydates as alerts when users click again on an expanded keydate.
    if (
      this.odSelections.isDayDrilldownActive &&
      date === this.odSelections.activeDrilldownDayAsString
    ) {
      this.clearActiveFilter(
        OD_FB_FILTERS.DAY,
        this.odSelections.activeDrilldownDayAsString,
      );
    } else {
      // Temp workaround to deal with keydates that a user has, but they are outside out of the datepicker range
      // We shift the datepicker range when clicking on a keydate in the feed (select the full month of the clicked keydate)

      const endOfData = this.datePickerMaxDate;

      let endRangeToUse = null;

      // Make sure we stick in the max allowed date range (otherwise the transition checks will interfere anyway)
      if (dayjs(date).endOf('month').isAfter(endOfData, 'day')) {
        endRangeToUse = endOfData;
      } else {
        endRangeToUse = dayjs(date).endOf('month');
      }

      // Update the daterange (select the full month of the keydate that was clicked)
      this.rangeChanged({
        start: dayjs(date).startOf('month'),
        end: endRangeToUse,
      });

      // Drilldown on the from date of the clicked-on keydate. Because we've switched the daterange, we can drilldown again.
      this.toggleOrSetSelectedDay(dayjs(date).format('YYYY-MM-DD'));
    }
  }

  // Action used to trigger the day drilldown on the Executive Dashboard
  @action
  updateSelectedDay(newDate) {
    this.setActiveFilters(OD_FB_FILTERS.DAY, newDate);
  }

  @action
  updateTopInsightChartTab(tabId) {
    this.topInsightChartTab = tabId;
  }
}
